import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import addSimulationBuyer from 'redux/actions/Common/addSimulationBuyer';
import BuyerContactFields, {
  EMPTY_CONTACT,
  buyerContactSchema,
  toBuyerContactPayload,
} from './BuyerContactFields';

const AddBuyerDialog = ({
  open,
  simulationId,
  simulationStatus,
  onClose,
  onAdded,
}) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const formik = useFormik({
    initialValues: { ...EMPTY_CONTACT },
    validationSchema: buyerContactSchema,
    onSubmit: (values) => {
      setSubmitting(true);
      setSubmitError(null);
      dispatch(
        addSimulationBuyer(
          simulationId,
          toBuyerContactPayload(values),
          (data) => {
            setSubmitting(false);
            formik.resetForm();
            onAdded(data);
          },
          (message) => {
            setSubmitting(false);
            setSubmitError(message);
          },
        ),
      );
    },
  });

  const handleClose = () => {
    if (submitting) return;
    formik.resetForm();
    setSubmitError(null);
    onClose();
  };

  const errorFor = (field) => formik.touched[field] && formik.errors[field];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add buyer</DialogTitle>
      <DialogContent>
        <Typography variant={'body2'} color={'text.secondary'} mb={2}>
          {simulationStatus === 'running'
            ? 'The new buyer joins the running simulation right away and starts ticking on its own schedule.'
            : 'The new buyer appears in the roster right away and starts ticking the next time the simulation is started or retriggered.'}
        </Typography>
        <BuyerContactFields
          contact={formik.values}
          onChange={(field, value) => {
            formik.setFieldValue(field, value);
            formik.setFieldTouched(field, true, false);
          }}
          errorFor={errorFor}
        />
        {submitError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={submitting}
          onClick={() => {
            // Touch every field so validation messages show on submit.
            formik.setTouched(
              { name: true, title: true, persona: true, email: true },
              false,
            );
            formik.handleSubmit();
          }}
        >
          {submitting ? (
            <>
              Adding...
              <CircularProgress size={16} sx={{ ml: 1 }} />
            </>
          ) : (
            'Add buyer'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBuyerDialog;
